import angularModule from 'Careers/angularModule/scripts/careers_module';
import template from 'Careers/angularModule/views/interview_modal.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { setupBrandNameProperties } from 'AppBranding';

const templateUrl = cacheAngularTemplate(angularModule, template);

// The calendar an applicant schedules on is resolved server-side (from their country tier) and
// passed down via the `calendly-base-url` attribute — see ProgramApplication#post_apply_interview_modal_calendly_url.
// We don't re-derive it from `currentUser.countryInternalTier` because that client value can be stale
// right after a location-derived country update (https://trello.com/c/Shpf2Wpy).
//
// The constants below are only a defensive fallback for the (unexpected) case where no server value
// was passed: T0 (US) users get the dedicated `qas-pat` calendar; everyone else gets the general
// `15min` calendar. The authoritative tier -> calendar mapping lives server-side and is shared with
// the `/interview` email-link redirect (QuanticRoutes.calendly_interview_url).
const CALENDLY_URL_T0 = 'https://calendly.com/quantic-interviews/qas-pat';
const CALENDLY_URL_DEFAULT = 'https://calendly.com/quantic-interviews/15min';
const T0_INTERNAL_TIER = 0;
const CALENDLY_SCRIPT_URL = 'https://assets.calendly.com/assets/external/widget.js';
const TRANSLATION_PREFIX = 'careers.post_apply_interview_modal';

function interviewModalFactory($injector) {
    const DialogModal = $injector.get('DialogModal');
    const $rootScope = $injector.get('$rootScope');
    const $document = $injector.get('$document');
    const $window = $injector.get('$window');
    const $sce = $injector.get('$sce');
    const safeDigest = $injector.get('safeDigest');
    const TranslationHelper = $injector.get('TranslationHelper');

    return {
        restrict: 'E',
        scope: {
            nextStepKey: '@',
            selectivityMessageKey: '@',
            calendlyBaseUrl: '@',
            programApplicationId: '@',
        },
        templateUrl,

        link(scope) {
            setupBrandNameProperties($injector, scope);

            Object.defineProperty(scope, 'currentUser', {
                get() {
                    return $rootScope.currentUser;
                },
            });

            // Initialize modal state
            scope.currentPage = 1;

            // Build Calendly URL with user info. The base calendar is resolved server-side and passed
            // down via `calendly-base-url`. Only if that's missing do we fall back to deriving it from
            // the (staleness-prone) client tier.
            const userEmail = scope.currentUser?.email || '';
            const userName = scope.currentUser?.name || '';
            const calendlyBaseUrl =
                scope.calendlyBaseUrl ||
                (scope.currentUser?.countryInternalTier === T0_INTERNAL_TIER ? CALENDLY_URL_T0 : CALENDLY_URL_DEFAULT);
            const calendlyParams = new URLSearchParams({
                hide_event_type_details: '1',
                hide_gdpr_banner: '1',
                email: userEmail,
                name: userName,
            });
            if (scope.programApplicationId) calendlyParams.set('utm_term', scope.programApplicationId);
            scope.calendlyUrl = `${calendlyBaseUrl}?${calendlyParams}`;

            // Resolve translations
            const translationHelper = new TranslationHelper(TRANSLATION_PREFIX);
            const fallbackHtml = translationHelper.get('calendly_fallback', {
                calendlyUrl: scope.calendlyUrl,
            });
            scope.calendlyFallbackHtml = $sce.trustAsHtml(fallbackHtml);

            const selectivityMessageHtml = translationHelper.get(scope.selectivityMessageKey);
            scope.selectivityMessageHtml = $sce.trustAsHtml(selectivityMessageHtml);

            scope.titleText = translationHelper.get('title');
            scope.nextStepText = translationHelper.get(scope.nextStepKey);
            scope.thankYouText = translationHelper.get('thank_you_message');
            scope.closeButtonText = translationHelper.get('close_button');

            // Load Calendly script
            loadCalendlyScript();

            // Listen for Calendly events
            function handleCalendlyEvent(e) {
                if (e.data && e.data.event === 'calendly.event_scheduled') {
                    goToPageTwo();
                }
            }

            $window.addEventListener('message', handleCalendlyEvent);

            // Clean up event listener when directive is destroyed
            scope.$on('$destroy', () => {
                $window.removeEventListener('message', handleCalendlyEvent);
            });

            function loadCalendlyScript() {
                // Check if script is already loaded
                if ($document[0].querySelector(`script[src="${CALENDLY_SCRIPT_URL}"]`)) {
                    return;
                }

                const script = $document[0].createElement('script');
                script.src = CALENDLY_SCRIPT_URL;
                script.async = true;
                $document[0].body.appendChild(script);
            }

            function goToPageTwo() {
                scope.currentPage = 2;
                safeDigest(scope);
            }

            scope.closeModal = () => {
                DialogModal.hideAlerts();
            };
        },
    };
}

angularModule.directive('interviewModal', ['$injector', interviewModalFactory]);
angularModule.directive('instantInterviewModal', ['$injector', interviewModalFactory]);
angularModule.directive('optionalInterviewModal', ['$injector', interviewModalFactory]);
